import { useAuth0 } from '@auth0/auth0-react'
import { Alert } from '@designsforhealth/dfh-react-components'
import { Button } from '@dfh-corp-sites/ui'
import { useLocation } from '@gatsbyjs/reach-router'
import { Typography } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import React, { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import { useResultWithLeftEffect } from '../../../../hooks/useResultWithLeftEffect'
import {
  latestCurrentDispensaryWithPatientFallback$,
  setCurrentDispensaryWithPatientFallbackInput,
  useCurrentDispensaryWithPatientFallback,
} from '../../../../lib/dispensaries/current-with-patient-fallback'
import { useDispensaryContext } from '../../../../lib/dispensaries/hooks/useDispensaryContext'
import { constNull } from '../../../../utils/constant'
import { pluckData } from '../../../../utils/result'
import * as Styled from '../../../products/details/ItemControls/styled'
import Spinner from '../../../Spinner'
import { RequestTestKitCallToAction } from '../DispensaryTestKitRequestCallToAction'

interface DispensaryTestKitRequestProps {
  productSku: string
}

const DispensaryTestKitRequestContent: React.FC<DispensaryTestKitRequestProps> = ({
  productSku,
}) => {
  const [status, dispensaryResult] = useCurrentDispensaryWithPatientFallback()
  const [, dispensary] = useResultWithLeftEffect(dispensaryResult, pluckData, constNull)
  const { loginWithRedirect } = useAuth0()
  const { control, setValue } = useFormContext()
  const location = useLocation()

  useEffect(() => {
    setValue('qty', 1)
  }, [setValue])

  if (status === 'rootSiteAnonymous' || status === 'currentDispensaryAnonymous') {
    // if anonymous, prompt login and redirect back to the current page

    return (
      <div className="flex justify-center flex-1 gap-2">
        <Typography
          variant="body2"
          className="text-brand-orange font-medium font-gotham flex-1 flex items-center"
        >
          Log in to request a test.
        </Typography>

        <Styled.DropdownCreatable
          id="qty"
          name="qty"
          menuPlacement="auto"
          control={control}
          disabled
          options={[]}
        />

        <Button
          key="login"
          size="sm"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            loginWithRedirect({
              screen_hint: 'login',
              appState: {
                returnTo: `${location.pathname}${location.search}${location.hash}`,
              },
            })
          }}
        >
          Login
        </Button>
      </div>
    )
  }
  if (!dispensary) {
    // ideally all users have active dispensary and this alert won't show
    return (
      <Alert type="informational" hideAlertIcon>
        Go to a Virtual Dispensary in order to request Spotlight Tests
      </Alert>
    )
  }
  // if in a dispensary or in root site but patient has active dispensary
  return <RequestTestKitCallToAction dispensary={dispensary} productSku={productSku} />
}

export const DispensaryTestKitRequest: React.FC<DispensaryTestKitRequestProps> = ({
  productSku,
}) => {
  const dispensaryContext = useDispensaryContext()

  useEffect(() => {
    setCurrentDispensaryWithPatientFallbackInput({ dispensaryContext })
  }, [dispensaryContext])

  return (
    <Subscribe source$={latestCurrentDispensaryWithPatientFallback$} fallback={<Spinner loading />}>
      <DispensaryTestKitRequestContent productSku={productSku} />
    </Subscribe>
  )
}
